/**
 * Firebase Web SDK configuration — read from Vite env vars so each
 * deployment (dev / staging / prod) can point at its own Firebase project
 * without code changes.
 *
 * The values come from Firebase Console → Project settings → General →
 * Your apps → Web. All six are public-safe (the browser bundle embeds
 * them anyway); the security model for FCM Web is the VAPID public key
 * plus the auth-token-issued sender ID, not these values.
 */

import { initializeApp, type FirebaseApp } from 'firebase/app'

export interface FirebaseConfig {
  apiKey: string
  authDomain: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
  appId: string
  /** GA4 measurement id (G-XXXXXXXXXX). Optional — only required for
   *  Firebase Analytics; FCM push and Performance work without it. */
  measurementId?: string
}

function readEnv(): FirebaseConfig {
  return {
    apiKey:            import.meta.env.VITE_FIREBASE_API_KEY            ?? '',
    authDomain:        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN        ?? '',
    projectId:         import.meta.env.VITE_FIREBASE_PROJECT_ID         ?? '',
    storageBucket:     import.meta.env.VITE_FIREBASE_STORAGE_BUCKET     ?? '',
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? '',
    appId:             import.meta.env.VITE_FIREBASE_APP_ID             ?? '',
    measurementId:     import.meta.env.VITE_FIREBASE_MEASUREMENT_ID     || undefined,
  }
}

/** VAPID public key from Firebase Console → Cloud Messaging → Web Push
 *  certificates. Without it `getToken()` rejects with
 *  `messaging/applicationServerKey is not valid`. */
export const VAPID_KEY: string = import.meta.env.VITE_FIREBASE_VAPID_KEY ?? ''

let app: FirebaseApp | null = null

/** Returns the initialised FirebaseApp, or null when config is missing —
 *  callers should degrade gracefully (admin web still works without push,
 *  just no tray notifications). */
export function getFirebaseApp(): FirebaseApp | null {
  if (app) return app
  const cfg = readEnv()
  // Surface a single console line so a misconfigured deploy is obvious in
  // browser dev-tools without throwing. Name every field that's empty so
  // the dev doesn't have to guess which VITE_FIREBASE_* the build missed
  // (and remember: Vite only reads .env at dev-server startup — editing
  // .env without restarting `pnpm dev` leaves the bundle on the old vars).
  const missing: string[] = []
  if (!cfg.apiKey)            missing.push('VITE_FIREBASE_API_KEY')
  if (!cfg.projectId)         missing.push('VITE_FIREBASE_PROJECT_ID')
  if (!cfg.messagingSenderId) missing.push('VITE_FIREBASE_MESSAGING_SENDER_ID')
  if (!cfg.appId)             missing.push('VITE_FIREBASE_APP_ID')
  if (missing.length > 0) {
    console.warn(
      '[firebase] config incomplete — push disabled. Missing: ' + missing.join(', ') +
      '. Restart `pnpm dev` after editing .env so Vite picks up the new values.',
    )
    return null
  }
  app = initializeApp(cfg)
  return app
}

/** True when the admin can register for web push. Treats a missing VAPID
 *  key as "not ready" — getToken would fail anyway. */
export function isFirebaseReady(): boolean {
  return getFirebaseApp() !== null && VAPID_KEY.length > 0
}
